import { zodResolver } from 'mantine-form-zod-resolver'
import { v1 as uuid } from 'uuid'
import { z } from 'zod'

import { EquipmentClass } from 'src/graphql/types/drivingExperiences'
import { AllLicenseClassValues } from 'src/graphql/types/licenses'
import { CountryConfig } from 'src/lib/country.utils'
import { isMedicalExamExpirationRequired } from 'src/lib/license.utils'
import { unformatPhoneNumber, removeCountryCode } from 'src/lib/phone.utils'
import { formatSsnString } from 'src/lib/ssn.utils'
import { dateOfBirthError, isSameDateOfBirth } from 'src/validations'

import {
  isAcceptableDotNumber,
  isAcceptableEmail,
} from '../employment/lib/validation'

export const defaultAddress = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  startDate: undefined,
  endDate: null,
}

export const defaultExperience = {
  employmentExperienceId: undefined,
  companyName: '',
  position: 'Truck Driver',
  unemploymentReason: '',
  reasonForLeaving: '',
  startDate: undefined,
  endDate: undefined,
  current: false,
  dotNumber: '',
  contactName: '',
  email: '',
  phone: '',
  faxNumber: '',
  isUnemployment: false,
  isDriverTerminated: false,
  isDriverSubjectToFMCSRs: true,
  isJobDesignatedAsSafetySensitiveFunctionUnderDOT: true,
  shouldContactEmployer: true,
  equipmentTypes: [],
  address: {
    street: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    startDate: undefined,
    endDate: undefined,
  },
}

export const defaultUnemployment = {
  employmentExperienceId: undefined,
  companyName: 'n/a',
  position: 'n/a',
  unemploymentReason: '',
  reasonForLeaving: 'n/a',
  startDate: undefined,
  endDate: undefined,
  isUnemployment: true,
  isDriverTerminated: false,
  isDriverSubjectToFMCSRs: true,
  isJobDesignatedAsSafetySensitiveFunctionUnderDOT: true,
  shouldContactEmployer: true,
}

export const schema = z.object({
  firstName: z
    .string()
    .min(1, { message: 'First name is required' })
    .max(100, { message: 'First name is too long' }),
  lastName: z
    .string()
    .min(1, { message: 'Last name is required' })
    .max(100, { message: 'Last name is too long' }),
  email: z.string().email({ message: 'Please enter a valid email' }),
  yearsOfExperience: z
    .number()
    .multipleOf(0.1)
    .max(100, { message: 'Please enter accurate information' })
    .optional(),
  phone: z
    .string()
    .refine(
      (data) => unformatPhoneNumber(removeCountryCode(data)).length === 10,
      {
        message: 'Phone number should have 10 digits',
      }
    ),
  dob: z
    .date({ invalid_type_error: 'This is not a valid date' })
    .superRefine((data, ctx) => {
      const message = dateOfBirthError(data)
      if (message) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message })
      }
    }),
  //https://stackoverflow.com/questions/75913987/validating-dates-with-zod-react-start-date-end-date
  issuingCountry: z.string(),
  // TODO: Add validation for ssn based on issuing country
  ssn: z
    .string()
    .min(11, {
      message: 'It should have minimum of 9 digits',
    })
    .max(11, {
      message: `It can't be more than 11 digits`,
    }),
  // Past dates are allowed: a driver's actual availability/hire date may
  // already have occurred, and the signed application must reflect reality.
  availableForWork: z
    .date({ invalid_type_error: 'Invalid date, MM/DD/YYYY' })
    .refine((data) => data >= new Date('01/01/2023'), {
      message: 'Please select a date in 2023 or later',
    })
    .nullish()
    .or(z.literal('')),
  addresses: z
    .array(
      z
        .object({
          city: z
            .string()
            .min(1, { message: 'City is required' })
            .max(100, { message: 'The name of your city is too long' }),
          state: z
            .string()
            .min(2, { message: 'State is required' })
            .max(50, { message: 'The name of your state is too long' }),
          zipCode: z
            .string()
            .min(5, { message: 'Postal code is required' })
            .max(100, {
              message: 'This is not a valid postal code',
            }),
          street: z
            .string()
            .min(1, {
              message: 'Street is required',
            })
            .max(500, { message: 'Your street name is too long' }),
          country: z
            .string()
            .min(1, { message: 'Country is required' })
            .max(100, { message: 'The name of your country is too long' }),
          startDate: z
            .date({
              invalid_type_error: 'Please enter a valid date (MM/YYYY)',
            })
            .refine((data) => data < new Date(), {
              message: 'You must select a date in the past',
            })
            .refine((data) => data > new Date('01/01/1930'), {
              message: 'Please select a date after 1930',
            }),
          endDate: z
            .date({
              invalid_type_error: 'Please enter a valid date (MM/YYYY)',
            })
            .refine((data) => data < new Date(), {
              message: 'You must select a date in the past',
            })
            .nullish(),
        })
        .refine(
          (data) => data.endDate === null || data.endDate >= data.startDate,
          {
            message: 'End date cannot be earlier than start date.',
            path: ['endDate'],
          }
        )
    )
    .optional(),
  equipment: z
    .array(
      z.object({
        type: z.nativeEnum({ ...EquipmentClass }).optional(),
        make: z.string().optional(),
        model: z.string().optional(),
        year: z.coerce
          .number()
          .int()
          .gte(1900, { message: 'Please enter a more recent year.' })
          .lte(2100, { message: 'Please enter a more recent year.' })
          .optional(),
        color: z.string().optional(),
        mileage: z.coerce
          .number()
          .int()
          .gte(0, { message: 'Please enter a valid number of miles.' })
          .lte(10000000, { message: 'Please enter a valid number of miles.' })
          .optional(),
        vin: z.string().optional(),
        weight: z.coerce
          .number()
          .int()
          .gte(0, { message: 'Please enter a valid weight.' })
          .lte(10000000, { message: 'Please enter a valid weight.' })
          .optional(),
        fifthWheelHeightInches: z.coerce
          .number()
          .int()
          .gte(0, { message: 'Please enter a valid height.' })
          .lte(10000000, { message: 'Please enter a valid height.' })
          .optional(),
      })
    )
    .optional(),
  license: z
    .object({
      licenseNumber: z.string().min(1, { message: 'This is required' }).max(40),
      licenseClass: z.nativeEnum(AllLicenseClassValues, {
        errorMap: () => {
          return { message: 'This field is required' }
        },
      }),
      expirationDate: z
        .date({ invalid_type_error: 'This is not a valid date' })
        .refine((data) => data > new Date('01/01/1930'), {
          message: 'Please select a date after 1930',
        })
        .refine(
          (data) => {
            const maxDate = new Date()
            maxDate.setFullYear(maxDate.getFullYear() + 50)
            return data < maxDate
          },
          {
            message: 'Please enter a valid expiration date',
          }
        ),
      medicalExamExpirationDate: z
        .date({ invalid_type_error: 'This is not a valid date' })
        .refine((data) => data > new Date('01/01/1930'), {
          message: 'Please select a date after 1930',
        })
        .nullish(),
      state: z
        .string({
          required_error: 'Please select a state from the dropdown',
        })
        .min(1, { message: 'Please select a state from the dropdown' }),
      // Carried so the medical-exam refine can tell US Class D (exempt) from
      // Mexican Class D (commercial freight — medical exam still required).
      country: z.string().optional(),
      // endorsements: z.array(z.nativeEnum(Endorsement)).optional(),
      isLicenseSuspended: z.boolean(),
      licenseSuspendedReason: z.string().optional(),
    })
    .refine(
      (data) =>
        !isMedicalExamExpirationRequired(data.licenseClass, data.country) ||
        Boolean(data.medicalExamExpirationDate),
      {
        message: 'This is required',
        path: ['medicalExamExpirationDate'],
      }
    )
    .refine(
      (data) =>
        !data.isLicenseSuspended ||
        (data.licenseSuspendedReason &&
          data.licenseSuspendedReason.trim().length > 0),
      {
        message: 'Please provide a reason for the license suspension.',
        path: ['licenseSuspendedReason'],
      }
    ),
  previousLicenses: z
    .array(
      z
        .object({
          licenseNumber: z
            .string()
            .min(1, { message: 'This is required' })
            .max(40),
          licenseClass: z.nativeEnum(AllLicenseClassValues, {
            errorMap: () => {
              return { message: 'This field is required' }
            },
          }),
          expirationDate: z
            .date({ invalid_type_error: 'This is not a valid date' })
            .refine((data) => data > new Date('01/01/1930'), {
              message: 'Please select a date after 1930',
            })
            .refine(
              (data) => {
                const maxDate = new Date()
                maxDate.setFullYear(maxDate.getFullYear() + 50)
                return data < maxDate
              },
              {
                message: 'Please enter a valid expiration date',
              }
            ),
          medicalExamExpirationDate: z
            .date({ invalid_type_error: 'This is not a valid date' })
            .refine((data) => data > new Date('01/01/1930'), {
              message: 'Please select a date after 1930',
            })
            .refine(
              (data) => {
                const maxDate = new Date()
                maxDate.setFullYear(maxDate.getFullYear() + 50)
                return data < maxDate
              },
              {
                message: 'Please enter a valid expiration date',
              }
            )
            .nullable()
            .optional(),
          state: z
            .string({
              required_error: 'Please select a state from the dropdown',
            })
            .min(1, { message: 'Please select a state from the dropdown' }),
          isLicenseSuspended: z.boolean().optional(),
          licenseSuspendedReason: z.string().optional(),
        })
        .refine(
          (data) =>
            !data.isLicenseSuspended ||
            (data.licenseSuspendedReason &&
              data.licenseSuspendedReason.trim().length > 0),
          {
            message: 'Please provide a reason for the license suspension.',
            path: ['licenseSuspendedReason'],
          }
        )
    )
    .optional(),
  twicCard: z.object({
    cardNumber: z
      .string()
      .length(8, { message: 'The TWIC CIN must be 8 digits long' })
      .nullish()
      .or(z.literal('')),
    expirationDate: z
      .date({ invalid_type_error: 'This is not a valid date' })
      .refine((data) => data > new Date('01/01/1930'), {
        message: 'Please select a date after 1930',
      })
      .nullish()
      .or(z.literal('')),
  }),
  accidents: z.array(
    z.object({
      accidentDate: z
        .date({ invalid_type_error: 'This is not a valid date' })
        .refine((data) => data < new Date(), {
          message: 'You must select a date in the past',
        })
        .refine((data) => data > new Date('01/01/1930'), {
          message: 'Please select a date after 1930',
        }),
      city: z
        .string({
          required_error: 'Please select a city from the dropdown',
        })
        .min(1, { message: 'Please select a city from the dropdown' })
        .max(100),
      state: z
        .string()
        .min(2, { message: 'Please select a city from the dropdown' })
        .max(100),
      description: z.string().min(1).max(1000),
      isPreventable: z.boolean(),
      isCommercialMotorVehicleInvolved: z.boolean(),
      involvedFatalities: z.boolean(),
      involvedInjuries: z.boolean(),
    })
  ),

  employmentExperiences: z.array(
    z
      .object({
        // Declared so the refines below can read it. Zod strips keys it does
        // not know, so an undeclared flag is invisible to a refine that needs it.
        isUnemployment: z.boolean().nullable().optional(),
        // Blank for a period of unemployment, which has no employer and no job
        // title. The accordion wrote "n/a" into both so this rule would pass;
        // the requirement is enforced below, for employment only.
        companyName: z.string().max(100),
        position: z.string().max(100),
        unemploymentReason: z.string().max(1000).optional(),
        reasonForLeaving: z.string().max(1000).optional().or(z.literal('')),
        dotNumber: z
          .string()
          .nullable()
          .optional()
          .refine(isAcceptableDotNumber, {
            message: 'DOT number must contain only numbers',
          }),
        email: z.string().nullable().optional().refine(isAcceptableEmail, {
          message: 'Please enter a valid email',
        }),
        // Nullable: an unanswered certification is a missing answer, not a
        // false one, and the column and the SDL have always allowed null. The
        // employment screen refuses to save an entry with one outstanding and
        // blocks Continue while any entry still needs one, so requiring a
        // boolean here only stopped the page silently — validateAllFields fails,
        // handleNext never fires, and nothing on screen says why.
        isDriverTerminated: z.boolean().nullable(),
        isDriverSubjectToFMCSRs: z.boolean().nullable(),
        shouldContactEmployer: z.boolean().nullable(),
        isJobDesignatedAsSafetySensitiveFunctionUnderDOT: z
          .boolean()
          .nullable(),
        startDate: z
          .date({ invalid_type_error: 'This is not a valid date' })
          .refine((data) => data < new Date(), {
            message: 'You must select a date in the past',
          })
          .refine((data) => data > new Date('01/01/1930'), {
            message: 'Please select a date after 1930',
          }),
        endDate: z
          .date({ invalid_type_error: 'This is not a valid date' })
          .refine((data) => data < new Date(), {
            message: 'You must select a date in the past',
          })
          .nullish(),
      })
      .refine((data) => data.isUnemployment || !!data.companyName?.trim(), {
        message: 'This is required',
        path: ['companyName'],
      })
      .refine((data) => data.isUnemployment || !!data.position?.trim(), {
        message: 'This is required',
        path: ['position'],
      })
      .refine(
        // `== null` rather than `=== null`: a current job has no end date, and
        // the form stores that as undefined. Comparing against null alone made
        // every current employer fail, which is what stopped Continue with
        // nothing on screen to explain it.
        (data) => data.endDate == null || data.endDate >= data.startDate,
        {
          message: 'End date must be after your start date.',
          path: ['endDate'],
        }
      )
      .refine(
        (data) =>
          data.isUnemployment ||
          data.endDate == null ||
          (!!data.reasonForLeaving && data.reasonForLeaving.length > 0),
        {
          message: 'This is required',
          path: ['reasonForLeaving'],
        }
      )
  ),
})

const resolveSchema = zodResolver(schema)

// The date already stored is accepted as-is. The personal step and the review
// page share this validator, so an old record can still be edited and signed.
// A different calendar day has to pass the rules.
export const validate = (values) => {
  const errors = resolveSchema(values)
  if (!errors?.dob || !isSameDateOfBirth(values?.dob, values?.persistedDob)) {
    return errors
  }
  const { dob: _storedDob, ...rest } = errors
  return Object.keys(rest).length ? rest : undefined
}

export const generateInitialValues = (application) => {
  const issuingCountry = application?.issuingCountry || CountryConfig.US.code
  const ssn = application?.ssn
    ? formatSsnString(issuingCountry, application.ssn)
    : ''

  return {
    firstName: application?.firstName || '',
    middleName: application?.middleName || undefined,
    lastName: application?.lastName || '',
    email: application?.email || '',
    phone: application?.phone || '',
    yearsOfExperience: application?.yearsOfExperience || undefined,
    dob: application?.dob ? new Date(application.dob) : undefined,
    // Kept off the save payload. Validation uses it to recognize the stored day.
    persistedDob: application?.dob,
    ssn,
    issuingCountry,
    availableForWork: application?.availableForWork
      ? new Date(application.availableForWork)
      : undefined,
    addresses:
      application?.addresses.length > 0
        ? application.addresses.map((add) => {
            return {
              city: add.city,
              state: add.state,
              zipCode: add.zipCode,
              street: add.street,
              country: add.country,
              startDate: add?.startDate
                ? new Date(add.startDate)
                : add.startDate,
              endDate: add?.endDate ? new Date(add.endDate) : add.endDate,
            }
          })
        : [],
    license: {
      licenseNumber: application?.license?.licenseNumber || '',
      licenseClass: application?.license?.licenseClass || '',
      state: application?.license?.state || '',
      country: application?.license?.country || CountryConfig.US.code,
      expirationDate: application?.license?.expirationDate
        ? new Date(application.license.expirationDate)
        : undefined,
      medicalExamExpirationDate: application?.license?.medicalExamExpirationDate
        ? new Date(application.license.medicalExamExpirationDate)
        : undefined,
      endorsements: application?.license?.endorsements || [],
      isLicenseSuspended: application?.license?.isLicenseSuspended || false,
      licenseSuspendedReason:
        application?.license?.licenseSuspendedReason || '',
    },
    previousLicenses:
      application?.previousLicenses?.length > 0
        ? application.previousLicenses.map((license) => ({
            id: license.licenseId,
            licenseNumber: license.licenseNumber || '',
            licenseClass: license.licenseClass || '',
            state: license.state || '',
            country: license.country || CountryConfig.US.code,
            expirationDate: license.expirationDate
              ? new Date(license.expirationDate)
              : undefined,
            medicalExamExpirationDate: license.medicalExamExpirationDate
              ? new Date(license.medicalExamExpirationDate)
              : undefined,
            endorsements: license.endorsements || [],
            isLicenseSuspended: license.isLicenseSuspended || false,
            licenseSuspendedReason: license.licenseSuspendedReason || '',
          }))
        : [],
    twicCard: {
      cardNumber: application?.twicCard?.cardNumber,
      expirationDate: application?.twicCard?.expirationDate
        ? new Date(application.twicCard.expirationDate)
        : undefined,
    },
    equipment:
      application?.equipment?.length > 0
        ? application?.equipment.map((equipment) => ({
            type: equipment?.type ?? undefined,
            make: equipment?.make ?? undefined,
            model: equipment?.model ?? undefined,
            year: equipment?.year ? equipment.year.toString() : undefined,
            color: equipment?.color ?? undefined,
            vin: equipment?.vin ?? undefined,
            weight: equipment?.weight ? equipment.weight.toString() : undefined,
            mileage: equipment?.mileage
              ? equipment.mileage.toString()
              : undefined,
            fifthWheelHeightInches: equipment.fifthWheelHeightInches
              ? equipment.fifthWheelHeightInches.toString()
              : undefined,
          }))
        : [],
    accidents:
      application?.accidents.length > 0
        ? application.accidents.map((accident) => ({
            key: accident.accidentId,
            accidentDate: new Date(accident.accidentDate),
            city: accident?.city || undefined,
            state: accident?.state || undefined,
            country: accident?.country || 'US',
            description: accident?.description || undefined,
            isPreventable: accident?.isPreventable || false,
            isCommercialMotorVehicleInvolved:
              accident?.isCommercialMotorVehicleInvolved || false,
            involvedFatalities: accident?.involvedFatalities || false,
            involvedInjuries: accident?.involvedInjuries || false,
          }))
        : [],
    employmentExperiences:
      application?.employmentExperiences.length > 0
        ? application.employmentExperiences.map((experience) => {
            const defaultItem = experience.isUnemployment
              ? defaultUnemployment
              : defaultExperience
            return {
              employmentExperienceId: experience.employmentExperienceId,
              companyName: experience.companyName ?? defaultItem.companyName,
              isUnemployment: experience?.isUnemployment,
              equipmentTypes: experience?.equipmentTypes ?? [],
              unemploymentReason:
                experience?.unemploymentReason ??
                defaultItem.unemploymentReason,
              position: experience?.position ?? defaultItem.position,
              reasonForLeaving:
                experience?.reasonForLeaving ?? defaultItem.reasonForLeaving,
              startDate: experience?.startDate
                ? new Date(experience.startDate)
                : experience.startDate,
              endDate: experience?.endDate
                ? new Date(experience.endDate)
                : experience.endDate,
              current: !experience.endDate,
              dotNumber: experience.dotNumber,
              contactName: experience.contactName,
              email: experience.email,
              phone: experience.phone,
              faxNumber: experience.faxNumber,
              isDriverTerminated:
                experience?.isDriverTerminated ??
                defaultItem.isDriverTerminated,
              isDriverSubjectToFMCSRs:
                experience?.isDriverSubjectToFMCSRs ??
                defaultItem.isDriverSubjectToFMCSRs,
              isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
                experience?.isJobDesignatedAsSafetySensitiveFunctionUnderDOT ??
                defaultItem.isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
              shouldContactEmployer:
                experience?.shouldContactEmployer ??
                (experience?.endDate
                  ? defaultItem.shouldContactEmployer
                  : false),
              address: {
                street:
                  experience.address?.street ||
                  defaultExperience.address.street,
                city:
                  experience.address?.city || defaultExperience.address.city,
                state:
                  experience.address?.state || defaultExperience.address.state,
                country:
                  experience.address?.country ||
                  defaultExperience.address.country,
                zipCode:
                  experience.address?.zipCode ||
                  defaultExperience.address.zipCode,
                startDate: experience.address?.startDate
                  ? new Date(experience.address.startDate)
                  : undefined,
                endDate: experience.address?.endDate
                  ? new Date(experience.address.endDate)
                  : undefined,
              },
            }
          })
        : [],
  }
}

/**
 * The form as the server has it: the saved application plus the empty
 * address and employer placeholders every step expects to find.
 */
export const savedFormValues = (application) => {
  const values = generateInitialValues(application)
  return {
    ...values,
    addresses: values.addresses.length ? values.addresses : [defaultAddress],
    employmentExperiences: values.employmentExperiences.length
      ? values.employmentExperiences
      : [{ ...defaultExperience, employmentExperienceId: uuid() }],
  }
}
